import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import './Home.css';

export default function Home({ employee }) {
  const [clockedIn, setClockedIn] = useState(false);
  const [currentTime, setCurrentTime] = useState('');
  const [attendance, setAttendance] = useState(null);
  const [todayRecord, setTodayRecord] = useState(null);
  const [loading, setLoading] = useState(true);

  const now = new Date();
  const month = now.getMonth() + 1;
  const year = now.getFullYear();
  const today = now.toISOString().split('T')[0];

  const initials = employee?.full_name
    ? employee.full_name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
    : 'AY';

  useEffect(() => {
    const tick = () => {
      const n = new Date();
      setCurrentTime(n.toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit' }));
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    loadAttendance();
  }, []);

  const loadAttendance = async () => {
    try {
      setLoading(true);
      const data = await api.getMyAttendance(month, year);
      setAttendance(data);
      const todayRec = data.records?.find(r => r.date?.startsWith(today));
      if (todayRec) {
        setTodayRecord(todayRec);
        setClockedIn(!!todayRec.clock_in && !todayRec.clock_out);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClock = async () => {
    if (clockedIn) {
      try {
        const res = await api.clockOut();
        if (res.error) { alert(res.error); return; }
        alert(`Clock-out recorded!\n\nTime: ${currentTime}\nTotal Hours: ${res.total_hours}\n\nHave a great evening!`);
        setClockedIn(false);
        loadAttendance();
      } catch (err) {
        alert('Error clocking out. Please try again.');
      }
    } else {
      navigator.geolocation.getCurrentPosition(async (pos) => {
        try {
          const res = await api.clockIn(
            pos.coords.latitude,
            pos.coords.longitude,
            null,
            null
          );
          if (res.error) {
            alert(`Clock-in failed!\n\n${res.error}\n\nReasons: ${res.reasons?.join(', ') || ''}`);
            return;
          }
          alert(`Clock-in successful!\n\nTime: ${currentTime}\nStatus: ${res.status}\nLate: ${res.late_minutes} minutes`);
          setClockedIn(true);
          loadAttendance();
        } catch (err) {
          alert('Error clocking in. Please try again.');
        }
      }, () => {
        alert('Location access denied. Please enable GPS to clock in.');
      });
    }
  };

  const summary = attendance?.summary || {};
  const records = attendance?.records || [];

  const formatTime = (ts) => {
    if (!ts) return '—';
    return new Date(ts).toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit' });
  };

  const getStatus = (record) => {
    if (!record.clock_in) return { label: 'Absent', cls: 'babsent', icon: '✗', iconCls: 'absent' };
    if (record.late_minutes > 0) return { label: `Late ${record.late_minutes}min`, cls: 'blate', icon: '⏱', iconCls: 'late' };
    return { label: 'Present', cls: 'bpresent', icon: '✓', iconCls: 'present' };
  };

  const LEAVES = [
    { type: 'Casual', remaining: 3, total: 3, color: 'red' },
    { type: 'Sick', remaining: 5, total: 5, color: 'green' },
    { type: 'Annual', remaining: 12, total: 14, color: 'blue' },
  ];

  return (
    <div className="home">
      <div className="clock-card">
        <div className="clock-left">
          <div className="clock-status-label">
            {now.toLocaleDateString('en-PK', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
          </div>
          <div className="clock-time">{currentTime}</div>
          <div className="clock-sub">{employee?.location_id ? `Location ${employee.location_id}` : 'Fotile Office'}</div>
        </div>
        <div className="clock-right">
          <div className="clocked-chip">
            <span className="live-dot"></span>
            {clockedIn ? `In at ${formatTime(todayRecord?.clock_in)}` : 'Not clocked in'}
          </div>
          <button className={`clock-btn ${clockedIn ? 'out' : 'in'}`} onClick={handleClock}>
            {clockedIn ? 'Clock Out' : 'Clock In'}
          </button>
        </div>
      </div>

      <div className="page-content">
        {todayRecord?.late_minutes > 0 && (
          <div className="notice">
            <svg width="15" height="15" fill="none" stroke="#92400e" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            You clocked in {todayRecord.late_minutes} minutes late today.
          </div>
        )}

        <div className="section-label">{now.toLocaleString('default', { month: 'long' })} {year} Summary</div>
        {loading ? (
          <div style={{textAlign:'center', padding:'20px', color:'var(--muted)'}}>Loading...</div>
        ) : (
          <div className="stat-grid">
            <div className="stat-tile green">
              <div className="tile-label">Days Present</div>
              <div className="tile-val green">{summary.present || 0}</div>
              <div className="tile-sub">this month</div>
            </div>
            <div className="stat-tile red">
              <div className="tile-label">Days Absent</div>
              <div className="tile-val red">{summary.absent || 0}</div>
              <div className="tile-sub">unauthorized</div>
            </div>
            <div className="stat-tile amber">
              <div className="tile-label">Lates</div>
              <div className="tile-val amber">{summary.lates || 0}</div>
              <div className="tile-sub">{summary.total_late_minutes || 0} min total</div>
            </div>
            <div className="stat-tile blue">
              <div className="tile-label">Leaves Left</div>
              <div className="tile-val blue" style={{fontSize:'18px'}}>20</div>
              <div className="tile-sub">of 20 total</div>
            </div>
          </div>
        )}

        <div className="section-label">Leave Balance</div>
        <div className="card" style={{marginBottom: 12}}>
          {LEAVES.map((l, i) => (
            <div key={i} className="leave-item">
              <div className="leave-row">
                <div className="leave-name">{l.type} Leave</div>
                <div className={`leave-count ${l.color}`}>{l.remaining} / {l.total}</div>
              </div>
              <div className="leave-bar">
                <div className="leave-fill" style={{
                  width: `${(l.remaining / l.total) * 100}%`,
                  background: l.color === 'green' ? 'var(--green)' : l.color === 'blue' ? 'var(--blue)' : 'var(--red)'
                }}></div>
              </div>
            </div>
          ))}
        </div>

        <div className="section-label">Recent Activity</div>
        <div className="card">
          {records.length === 0 ? (
            <div style={{padding:'20px', textAlign:'center', color:'var(--muted)', fontSize:'13px'}}>
              No attendance records this month
            </div>
          ) : records.slice(0, 5).map((r, i) => {
            const st = getStatus(r);
            return (
              <div key={i} className="activity-item">
                <div className={`act-icon ${st.iconCls}`}>{st.icon}</div>
                <div className="act-info">
                  <div className="act-date">{new Date(r.date).toLocaleDateString('en-PK', {weekday:'short', day:'numeric', month:'short'})}</div>
                  <div className="act-time">In: {formatTime(r.clock_in)} · Out: {formatTime(r.clock_out)}</div>
                </div>
                <div className="act-right">
                  <div className="act-hours">
                    {r.total_minutes ? `${Math.floor(r.total_minutes/60)}h ${r.total_minutes%60}m` : 'ongoing'}
                  </div>
                  <span className={`badge ${st.cls}`}>{st.label}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}